import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
    Autocomplete,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import cc from 'currency-codes'
import PropTypes from 'prop-types'

import LanguageSelect from '@/common/components/LanguageSelect/LanguageSelect'
import { getSampleFormattedCurrencyString } from '@/common/utils'

import { FORM_FIELDS, TECHNICAL_LABELS, TEST_IDS } from '../constants/formConstants'

const TechnicalTypography = styled(Typography)({
    letterSpacing: '0.5px',
})

const classes = {
    input: {},
    inputLabel: {},
    selectInput: {},
}

/**
 * DefaultSettingsSection - Organization default settings fields
 *
 * Collects:
 * - Unit system (metric/imperial)
 * - Language preference
 * - Currency code
 * - Currency preview (read-only)
 * - Optional voucher code
 *
 * @param {Object} props
 * @param {Object} props.control - react-hook-form control object
 * @param {Function} props.watch - react-hook-form watch function
 * @param {Function} props.getValues - react-hook-form getValues function
 * @param {Object} props.theme - MUI theme object
 * @param {boolean} props.showVoucherCode - Whether to show voucher code field (feature flag)
 * @param {boolean} props.isResellerEnv - Whether this is a reseller environment
 * @param {string} props.debouncedVoucherCode - Debounced voucher code value
 * @param {boolean} props.isValidVoucherCode - Whether the voucher code is valid
 */
export const DefaultSettingsSection = ({
    control,
    debouncedVoucherCode,
    getValues,
    isResellerEnv,
    isValidVoucherCode,
    showVoucherCode,
    theme,
    watch,
}) => {
    const { t } = useTranslation()

    const units = [
        {
            name: t('millimetres, metres, kilograms'),
            value: 'metric',
        },
        {
            name: t('inches, feet, pounds'),
            value: 'imperial',
        },
    ]

    return (
        <Stack spacing={2}>
            <TechnicalTypography
                color={theme.palette.text.secondary}
                sx={{ textTransform: 'uppercase', mb: 1 }}
                variant="strong2"
            >
                {t(TECHNICAL_LABELS.SECTION_SETTINGS)}
            </TechnicalTypography>

            {/* Unit System */}
            <Controller
                control={control}
                name={FORM_FIELDS.ORGANISATION_UNITS}
                render={({ field: { onBlur, onChange, value }, fieldState }) => (
                    <FormControl fullWidth>
                        <InputLabel
                            error={fieldState.invalid}
                            id="unit-select-label"
                        >
                            {t('System of measurement')}
                        </InputLabel>
                        <Select
                            id="unit-select"
                            label={t('System of measurement')}
                            labelId="unit-select-label"
                            placeholder={t('Select units')}
                            value={value}
                            onBlur={onBlur}
                            onChange={(e) => onChange(e.target.value)}
                        >
                            {units.map((unit) => (
                                <MenuItem
                                    key={unit.value}
                                    value={unit.value}
                                >
                                    {unit.name}
                                </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText error={fieldState.invalid}>{t(fieldState.error?.message || '')}</FormHelperText>
                    </FormControl>
                )}
                rules={{ required: t('Units is required') }}
            />

            {/* Language */}
            <Controller
                control={control}
                name={FORM_FIELDS.ORGANISATION_LANGUAGE}
                render={({ field: { onBlur, onChange, value }, fieldState }) => (
                    <LanguageSelect
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message || ''}
                        id="language-select"
                        label={t('Language')}
                        value={value}
                        onBlur={onBlur}
                        onChange={onChange}
                    />
                )}
                rules={{ required: t('Language is required') }}
            />

            {/* Currency */}
            <Controller
                control={control}
                name={FORM_FIELDS.ORGANISATION_CURRENCY_CODE}
                render={({ field: { onBlur, onChange, value }, fieldState }) => (
                    <Autocomplete
                        disableClearable={true}
                        options={cc.codes()}
                        renderInput={(params) => (
                            <TextField
                                error={fieldState.invalid}
                                helperText={fieldState.error?.message || ''}
                                {...params}
                                InputLabelProps={{
                                    sx: classes.inputLabel,
                                    'data-testid': TEST_IDS.CURRENCY_LABEL,
                                }}
                                inputProps={{
                                    ...params.inputProps,
                                    'data-testid': TEST_IDS.CURRENCY_INPUT,
                                }}
                                label={t('Currency')}
                                fullWidth
                            />
                        )}
                        value={value}
                        fullWidth
                        onBlur={onBlur}
                        onChange={(e, newValue) => {
                            onChange(newValue)
                        }}
                    />
                )}
                rules={{ required: t('Currency is required') }}
            />

            {/* Currency Preview */}
            <TextField
                InputLabelProps={{
                    'data-testid': TEST_IDS.CURRENCY_PREVIEW_LABEL,
                }}
                InputProps={{
                    readOnly: true,
                }}
                inputProps={{
                    sx: classes.selectInput,
                    'data-testid': TEST_IDS.CURRENCY_PREVIEW,
                }}
                label={t('Currency preview')}
                value={
                    !watch(FORM_FIELDS.ORGANISATION_LOCALE) || !watch(FORM_FIELDS.ORGANISATION_CURRENCY_CODE)
                        ? ''
                        : getSampleFormattedCurrencyString(
                              getValues(FORM_FIELDS.ORGANISATION_LOCALE),
                              getValues(FORM_FIELDS.ORGANISATION_CURRENCY_CODE)
                          )
                }
                fullWidth
            />

            {/* Voucher Code (Conditional) */}
            {showVoucherCode && !isResellerEnv ? (
                <Controller
                    control={control}
                    name={FORM_FIELDS.ORGANISATION_VOUCHER_CODE}
                    render={({ field: { onBlur, onChange, value } }) => (
                        <TextField
                            error={debouncedVoucherCode ? !isValidVoucherCode : null}
                            helperText={
                                debouncedVoucherCode && !isValidVoucherCode
                                    ? t('Invalid promotion or voucher code')
                                    : null
                            }
                            inputProps={{
                                sx: classes.input,
                                'data-testid': TEST_IDS.VOUCHER_CODE,
                            }}
                            label={t('Voucher code')}
                            sx={classes.input}
                            value={value}
                            fullWidth
                            onBlur={onBlur}
                            onChange={onChange}
                        />
                    )}
                />
            ) : null}
        </Stack>
    )
}

DefaultSettingsSection.propTypes = {
    control: PropTypes.object.isRequired,
    getValues: PropTypes.func.isRequired,
    isResellerEnv: PropTypes.bool.isRequired,
    showVoucherCode: PropTypes.bool.isRequired,
    theme: PropTypes.object.isRequired,
    watch: PropTypes.func.isRequired,
    debouncedVoucherCode: PropTypes.string,
    isValidVoucherCode: PropTypes.bool,
}
